import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, retry, shareReplay } from 'rxjs/operators';
import { EComplaintStatus, ESeverity } from '../../utils/enums';
import { ApiService } from './api.service';
import { IFollowUp, ISupportingDocument } from './complaints.service';

export interface ICase {
  title: string;
  description: string;
  location: ILocation;
  contact: IContact;
  categoryIds: string[];
  organizationIds: string[];
  severity: string;
  trackingLevel: string;
  estimatedResolutionDays: number;
  supportingDocuments: ISupportingDocument[];
  primaryOrganizationId: string;
}

export interface ICaseContent {
  id: string;
  title: string;
  status: EComplaintStatus;
  assignedTo: string;
  createdAt: Date;
  applicationNumber: string;
}

export interface ICaseDetail {
  id: string;
  title: string;
  description: string;
  location: ILocation;
  trackingLevel: string;
  contact: IContact;
  status: EComplaintStatus;
  caseSeverity: ESeverity;
  estimatedResolutionDays: number;
  categories: ICategory[];
  documents: {
    type: string;
    url: string;
    fileName: string;
  }[];
  followUps: IFollowUp[];
  organizations: any[];
  assignedTo: string;
  createdAt: Date;
  urgent: boolean;
  timeline: Timeline[];
  primaryOrganization: {
    organizationName: string;
    organizationId: string;
  };
  applicationNumber: string;
  reviews: IReview[];
}

export interface Timeline {
  action: string;
  actionMetadata: ActionMetadata;
  visibility: 'PUBLIC' | 'PRIVATE';
  userDetails?:{
    fullName:string;
    email:string;
    phoneNumber:string;
    occupation:string;
    profilePictureUrl:string;
    trackingLevel:string;
  }
  userId: string;
  createdAt?: Date; // Make it optional since API doesn't always provide it
}

export interface IReview {
  rating: number;
  comment: string;
  createdAt: string;
}
export interface ActionMetadata {
  reportedBy?: string;
  createdBy?: string;
  comment?: string;
  CommentedBy?: string;
  invitedBy?: string;
  invitationNote?: string;
  inviteeActions?: any[]; //TODO: Should update this type
  invitedOrganizations?: string[];
  supportingDocuments?: SupportingDocument[];
  escalatedBy?: string;
  trackingLevel?: string;
  primaryInstitutionName?: string;
}

export interface SupportingDocument {
  filename: string;
  url: string;
}

export interface ICategory {
  id: string;
  name: string;
  description: string;
}

interface ICaseDetailResponse {
  message: string;
  payload: ICaseDetail;
}

export interface IContact {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  documentNumber?: string;
  documentType?: string;
  gender?: string;
}

export interface ILocation {
  districtId: string;
  districtName?: string;
  sectorId: string;
  sectorName?: string;
  cellId: string;
  cellName?: string;
  villageId: string;
  villageName?: string;
}

export interface ICasePayload {
  content: ICaseContent[];
  page: IPage;
}

export interface ICreateCaseResponse {
  message: string;
  payload: {
    caseId: string;
    caseNumber: string;
  };
}

export interface ICaseTypeResponse {
  message: string;
  payload: ICaseType[];
}

export interface ICaseType {
  id: string;
  name: string;
}

export interface IStartCase {
  categoryIds: string[];
  organizationIds: string[];
  severity: string;
  trackingLevel: string;
  estimatedResolutionDays: number;
  primaryOrganizationId: string;
}

interface IPage {
  size: number;
  totalElements: number;
  totalPages: number;
  number: number;
}

export interface ICaseResponse {
  message: string;
  payload: ICasePayload;
}

export interface IAddCommentResponse {
  message: string;
  payload: {
    message: string;
  };
}

export interface IUpdateCaseStatusResponse {
  message: string;
  payload: string;
}

export interface IEditCasePayload {
  categoryIds: string[];
  organizationIds: string[];
  primaryOrganizationId: string;
  severity: string;
  trackingLevel: string;
  estimatedResolutionDays: number;
}

export interface IEditCaseResponse {
  message: string;
  payload: ICaseDetail;
}

export interface IRespondToInvitationResponse {
  message: string;
  payload: {
    message: string;
  };
}

export interface IRateComplaintResponse {
  payload: IRateComplaintPayload;
  message: string;
}

export interface IRateComplaintPayload {
  reviewId: string;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class CasesService {
  constructor(private api: ApiService) {}

  getCasesPerCitizen(
    page: number,
    size: number,
    title?: string,
  ): Observable<ICaseResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (title) {
      params = params.set('searchTerm', title);
    }

    return this.api.GET<ICaseResponse>('cases/citizen', { params });
  }

  getCaseById(caseId: string): Observable<ICaseDetail> {
    return this.api
      .GET<ICaseDetailResponse>(`cases/${caseId}`)
      .pipe(map((response) => response.payload));
  }

  getUrgentCases(page: number, size: number): Observable<ICaseResponse> {
    const params = new HttpParams()
      .set('isUrgent', 'true')
      .set('page', page.toString())
      .set('size', size.toString());

    return this.api.GET<ICaseResponse>('cases', { params }).pipe(
      map((response) => {
        if (response?.payload) {
          return response;
        }
        return {
          message: 'No urgent cases found',
          payload: {
            content: [],
            page: {
              size,
              totalElements: 0,
              totalPages: 0,
              number: page,
            },
          },
        };
      })
    );
  }

  getCaseTypes(): Observable<ICaseType[]> {
    return this.api.GET<ICaseTypeResponse>('categories').pipe(
      map((response) => response.payload),
      retry(1),
      catchError(() => of([])),
      shareReplay(1)
    );
  }

  createCase(caseData: ICase): Observable<ICreateCaseResponse> {
    return this.api.POST<ICreateCaseResponse>('cases', caseData);
  }

  startCase(
    complaintId: string,
    caseData: IStartCase
  ): Observable<ICreateCaseResponse> {
    return this.api.POST<ICreateCaseResponse>(
      `cases/${complaintId}/start-case`,
      caseData
    );
  }

  getCasesByOrganizationId(id: string): Observable<ICaseContent[]> {
    const params = new HttpParams().set('institutionId', id);
    return this.api
      .GET<ICaseResponse>('cases', { params })
      .pipe(map((response) => response.payload.content));
  }

  getCasesByCitizenId(
    id: string,
    page: number,
    size: number,
    search: string
  ): Observable<ICaseContent[]> {
    const params = new HttpParams()
      .set('id', id)
      .set('page', page.toString())
      .set('size', size.toString())
      .set('searchTerm', search);

    return this.api.GET<ICaseResponse>(`cases/citizen/${id}`, { params }).pipe(
      map((response) => {
        if (response?.payload?.content) {
          return response.payload.content;
        }
        return [];
      })
    );
  }

  addComment(
    caseId: string,
    comment: string,
    supportingDocuments: ISupportingDocument[],
    visibility: string
  ): Observable<IAddCommentResponse> {
    return this.api.POST<IAddCommentResponse>(`cases/${caseId}/comments`, {
      comment,
      supportingDocuments,
      visibility,
    });
  }

  updateCaseStatus(
    caseId: string,
    status: EComplaintStatus,
    comment: string
  ): Observable<IUpdateCaseStatusResponse> {
    const body = {
      status,
      comment,
      supportingDocuments: [],
    };
    return this.api.PATCH<IUpdateCaseStatusResponse>(
      `cases/${caseId}/staff/case-status`,
      body
    );
  }

  downloadCases(): Observable<Blob> {
    const params = new HttpParams().set('fileFormat', 'EXCEL');
    return this.api.downloadFile('cases/download', params);
  }

  downloadCasesByCitizenId(id: string, search: string): Observable<Blob> {
    const params = new HttpParams()
      .set('fileFormat', 'EXCEL')
      .set('searchTerm', search);
    return this.api.downloadFile(`cases/citizen/${id}/download`, params);
  }

  editCase(
    caseId: string,
    caseData: IEditCasePayload
  ): Observable<IEditCaseResponse> {
    return this.api.PATCH<IEditCaseResponse>(`cases/${caseId}`, caseData);
  }

  rateComplaint(
    applicationNumber: string,
    rating: number,
    comment?: string
  ): Observable<IRateComplaintPayload> {
    return this.api
      .POST<IRateComplaintResponse>('cases/review', {
        rating,
        applicationNumber,
        comment,
      })
      .pipe(map((response) => response.payload));
  }
}